import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { Loader } from 'src/domains/layouts/webview/components/loader/Loader';
import { EventHeader } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventGroupHeaderUnified';
import { withConfig } from 'src/withConfig';

const columsStyles = (theme: EmotionTheme): SerializedStyles => css`
    & [class$='SelectionHeader'] {
        display: none;
    }

    & [class$='SelectionsGroupLiItem'] {
        flex-flow: row;
        padding: 8px;
        border-radius: 0;
        border-bottom: 1px solid ${theme.star.unified.eventRow.borderColor};
        gap: ${theme.star.gap.small};
    }

    & [class$='SelectionsGroupName'] {
        display: flex;
        padding: 0 4px;
        justify-content: flex-start;
        background: none;
        min-height: 44px;
        border: none;
        min-width: 0;
    }
`;


export const SelectionsGroupLiItem = withConfig(theme => styled('li', { label: 'SelectionsGroupLiItem' })`
    border-radius: ${theme.star.radius.medium};
    flex: 1 1 0;
    background-color: ${theme.star.selectionsGroup.bgColor};
    color: ${theme.star.selectionsGroup.txtColor};
    display: flex;
    justify-content: space-between;
    flex-flow: column;
    align-items: center;
`);

export const SelectionsGroupName = withConfig(theme => styled('div', { label: 'SelectionsGroupName' })`
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid ${theme.star.selectionsGroup.borderColor};
    display: flex;
    flex: 1 1 auto;
    font-size: ${theme.star.selectionsGroup.typography.fontSizePrimary};
    font-weight: ${theme.star.selectionsGroup.typography.fontWeightPrimary};
    line-height: ${theme.star.selectionsGroup.typography.lineHeightPrimary};
    min-height: 32px;
    width: 100%;

    & > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`);

export const SelectionsGroupWrapper = withConfig(theme => styled('div', { label: 'SelectionsGroupWrapper' })`
    display: flex;
    gap: ${theme.star.gap.small};
    padding: ${theme.star.padding.medium};
    border-bottom: 1px solid ${theme.star.unified.selection.borderColor};

    & [class$='SelectionsGroupName'] {
        display: none;
    }

    & [class$='SelectionItemStyle'] {
        align-self: stretch;
    }
`);

export const SelectionsGroup = withConfig(theme => styled('section', { label: 'SelectionsGroup' })`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    gap: ${theme.star.gap.small};

    & [class$='SelectionsGroupName'] {
        font-size: ${theme.star.fontSize.xRegular};
        font-weight: ${theme.star.fontWeight.xMedium};
        line-height: ${theme.star.lineHeight[250]};
    }

    &.selection-group--columns {
        & [class$='SelectionGroupListVersion'] {
            gap: 0;
        }

        & [class$='SelectionsGroupWrapper'] {
            padding: 0;
            flex-flow: column;
            border: none;
        }

        ${columsStyles(theme)}
    }

    /* Football multi-cell markets: column on mobile, horizontal row from tablet up */
    &.selection-group--stack-on-mobile {
        & [class$='SelectionHeader'] {
            display: none;
        }

        & [class$='SelectionGroupListVersion'] {
            gap: 0;
        }

        & [class$='SelectionsGroupWrapper'] {
            flex-flow: column;
            gap: 0;
            padding: 0;
            border: none;

            & [class$='SelectionsGroupName'] {
                display: flex;
                padding: 0 4px;
                justify-content: flex-start;
                background: none;
                min-height: 44px;
                border: none;
                min-width: 0;
            }
        }

        & [class$='SelectionsGroupLiItem'] {
            flex-flow: row;
            padding: 8px;
            border-radius: 0;
            border-bottom: 1px solid ${theme.star.unified.eventRow.borderColor};
        }

        @media ${theme.star.mediaQuery.tablet} {
            & [class$='SelectionHeader'] {
                display: flex;
            }

            & [class$='SelectionGroupListVersion'] {
                gap: ${theme.star.gap.small};
            }

            & [class$='SelectionsGroupWrapper'] {
                flex-flow: row;
                gap: ${theme.star.gap.small};
                padding: ${theme.star.padding.medium};
                border-bottom: 1px solid ${theme.star.unified.selection.borderColor};

                & [class$='SelectionsGroupName'] {
                    display: none;
                }
            }

            & [class$='SelectionsGroupLiItem'] {
                flex-flow: column;
                padding: 0;
                border-radius: ${theme.star.radius.medium};
                border-bottom: none;
            }
        }
    }
`);

export const SelectionsGroupWrap = styled('div', { label: 'SelectionsGroupWrap' })`
    border-left: none;
    display: flex;
    flex-flow: column nowrap;
    margin-bottom: -1px;
`;

export const SelectionHeader = styled(EventHeader, { label: 'SelectionHeader' })`
    display: flex;
    justify-content: space-around;
    gap: 4px;
    
    & [class$='EventHeaderCaption'] {
        width: 100%;
    }
`;

export const SelectionsGroupList = styled('ul', { label: 'SelectionsGroupList' })`
    display: flex;
    flex: 1 1 auto;
    margin: 0;
    padding: 0;
    width: 100%;
    border-right: 0;
    flex-flow: column nowrap;
`;

interface EmptyPropsType {
    selectionsCount: number;
}


export const Empty = styled('div', { label: 'Empty' })<EmptyPropsType>`
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
`;

export const MarketLoaderWrapper = styled('div', { label: 'MarketLoaderWrapper' })`
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
`;

export const MarketLoader = styled(Loader, { label: 'MarketLoader' })`
    fill: currentcolor;
    width: 10px;
`;

interface SelectionGroupListVersionPropsType {
    columns: number;
}

const setListVersion = (theme: EmotionTheme, columns: number): SerializedStyles => {
    if (columns === 1) {
        return css`
            gap: 0;

            ${columsStyles(theme)};

            li {
                margin-right: 0 !important; /* Probably to remove */
            }
        `;
    }

    return css`
        display: grid;
        grid-template-columns: minmax(0, 1fr);

        @media ${theme.star.mediaQuery.tablet} {
            grid-template-columns: repeat(${columns}, minmax(0, 1fr));
        }
    `;
};

export const SelectionGroupListVersion = withConfig(theme => styled('ul', { label: 'SelectionGroupListVersion' })<SelectionGroupListVersionPropsType>`
    align-self: flex-start;
    display: flex;
    flex-flow: column nowrap;
    gap: ${theme.star.gap.small};
    width: 100%;
    ${({ columns }): SerializedStyles => setListVersion(theme, columns)}; /* This function overwrites default styles above. */
`);
